/**
 * Plural selection at the render boundary.
 *
 * The repo had no plural mechanism at all, so this sets the precedent. Three
 * choices worth writing down:
 *
 * 1. **`Intl.PluralRules` picks the form; the dictionaries own the text.** The
 *    alternative — `count === 1 ? a : b` in a component — puts a *language
 *    rule* in the UI layer, which is the same mistake as putting a translated
 *    string in the domain layer. Swedish and English both need only `one` and
 *    `other`, but the platform already knows that and knows the locales that
 *    do not.
 *
 * 2. **One key per form, suffixed `_one` / `_other`.** A translator sees two
 *    complete sentences instead of a fragment plus a rule, and the pair is
 *    greppable.
 *
 * 3. **One phrase per pluralised noun, joined by a template.** A message with
 *    two independently-counted nouns ("3 seats, 1 lying place") cannot be one
 *    template — each noun needs its own decision, and the join order belongs to
 *    the market. That is why the capacity badge composes two phrases rather
 *    than filling one sentence.
 *
 * This is a DISPLAY concern, exactly like unit formatting: the domain keeps
 * returning keys and numbers (`calculateSeatingCapacity` still hands back
 * `{seats, lyingSpots}`), and nothing here reaches it.
 */
import type { Dictionary } from "./types";

/**
 * The plural forms this app's dictionaries carry. Swedish and English both use
 * exactly these two; a locale that needs `few`/`many` would need new keys, so
 * anything the platform reports outside `one` collapses to `other` rather than
 * silently indexing a key that does not exist.
 */
export type PluralForm = "one" | "other";

/** Base keys that exist in `_one` / `_other` pairs. */
export type PluralBaseKey =
	| "plan_view_capacitySeats"
	| "plan_view_capacityLying"
	| "workspace_badgeRows"
	| "workspace_badgeRulesFlags";

const RULES_CACHE: Record<string, Intl.PluralRules> = {};

function rulesFor(locale: string): Intl.PluralRules {
	if (!RULES_CACHE[locale]) {
		try {
			RULES_CACHE[locale] = new Intl.PluralRules(locale);
		} catch {
			// An unknown locale tag must not take the render down.
			RULES_CACHE[locale] = new Intl.PluralRules("en");
		}
	}
	return RULES_CACHE[locale];
}

/**
 * Which form a count takes in a locale.
 *
 * Note that ZERO is `other` in both Swedish and English — "0 sittplatser" and
 * "0 seats" — which is why this asks the platform instead of testing
 * `count === 1`.
 */
export function pluralForm(count: number, locale: string): PluralForm {
	const category = rulesFor(locale).select(count);
	return category === "one" ? "one" : "other";
}

/**
 * Resolve a counted phrase: picks the `_one` / `_other` variant and substitutes
 * `{count}`. The number is formatted for the locale, so a Swedish thousand
 * separator is a space and an English one a comma.
 */
export function pluralizeCount(
	t: Dictionary,
	base: PluralBaseKey,
	count: number,
	locale: string,
): string {
	const key = `${base}_${pluralForm(count, locale)}` as keyof Dictionary;
	const template = t[key] as string;
	return template.replaceAll(
		"{count}",
		new Intl.NumberFormat(locale).format(count),
	);
}
